import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { ThemeProvider } from '@material-ui/core/styles';
import { selectMainTheme } from 'app/store/fuse/settingsSlice';
import { Dialog, DialogContent, DialogTitle } from '@material-ui/core';
import { format } from 'date-fns';
import { hu } from 'date-fns/locale';

import ConfigurableDataTable from 'app/admin/common/component/ConfigurableDataTable';
import { Button } from './Pipeline';

import { useOptimizationPipeline } from '../OptimizationPipelineProvider';

export default function ModelValidationHistoryPopup() {
    const { setPopup, modelValidationLogDataList } = useOptimizationPipeline();
    const mainTheme = useSelector(selectMainTheme);

    const toText = (val) => {
        if (val == null) return '-';
        if (val instanceof Date) return val.toLocaleString('hu-HU');
        if (typeof val === 'object') return JSON.stringify(val);
        return String(val);
    };

    const formatApe = (x) => {
        const n = typeof x === 'number' ? x : Number(String(x).replace(',', '.'));
        return Number.isFinite(n) ? n.toFixed(4) : toText(x);
    };

    const formatDate = (v) => {
        if (!v) return '-';
        const d = v instanceof Date ? v : new Date(v);
        const t = d.getTime();
        return Number.isNaN(t) ? '-' : format(d, 'yyyy.MM.dd.', { locale: hu });
    };

    const getAllFeatureKeys = (list) => {
        const set = new Set();
        for (const entry of (Array.isArray(list) ? list : [])) {
            const fx = entry?.features && typeof entry.features === 'object' ? entry.features : null;
            if (fx) Object.keys(fx).forEach((k) => set.add(k));
        }
        return Array.from(set);
    };

    const featureCell = (row, key) => {
        const v = row?.features?.[key];

        const ape = typeof v === 'object' && v ? v.ape : v;
        const tol = typeof v === 'object' && v ? v.tolerance : undefined;
        const acc = typeof v === 'object' && v ? v.accurate : undefined;

        const bg = acc === true ? 'bg-green-100 text-green-900' : acc === false ? 'bg-red-100 text-red-900' : 'bg-gray-100 text-gray-500';
        const accText = acc === true ? 'Yes' : acc === false ? 'No' : '-';

        return (
            <div className={'flex flex-col text-12 px-5 py-3 rounded ' + bg}>
                <div className='leading-4'>
                    <span className='font-semibold opacity-60'>APE:</span> {formatApe(ape)}
                </div>
                <div className='leading-4'>
                    <span className='font-semibold opacity-60'>Tolerance:</span> {toText(tol)}
                </div>
                <div className='leading-4'>
                    <span className='font-semibold opacity-60'>Accurate:</span>{' '}
                    <span className='font-semibold'>{accText}</span>
                </div>
            </div>
        );
    };

    const overallAccurate = (row) => {
        if (typeof row?.accurate === 'boolean') return row.accurate;
        if (typeof row?.meta?.accurate === 'boolean') return row.meta.accurate;

        const fx = row?.features && typeof row.features === 'object' ? row.features : null;
        if (fx) {
            let seen = false;
            for (const k of Object.keys(fx)) {
                const a = fx[k]?.accurate;
                if (typeof a === 'boolean') {
                    seen = true;
                    if (a === false) return false;
                }
            }
            if (seen) return true;
        }
        return undefined;
    };

    const accurateCell = (row) => {
        const v = overallAccurate(row);
        const bg = v === true ? 'bg-green-100 text-green-900' : v === false ? 'bg-red-100 text-red-900' : 'bg-gray-100 text-gray-500';
        const text = v === true ? 'Yes' : v === false ? 'No' : '-';
        return (
            <div className={'flex items-center justify-center text-12 px-5 py-3 rounded ' + bg}>
                <span className='font-semibold'>{text}</span>
            </div>
        );
    };

    const columns = useMemo(() => {
        const base = [
            {
                key: 'ts',
                header: 'Timestamp',
                sortable: true,
                accessor: (row) => formatDate(row?.ts),
            },
            {
                key: '_accurate',
                header: 'Accurate',
                accessor: (row) => accurateCell(row),
            },
        ];
        const featureKeys = getAllFeatureKeys(modelValidationLogDataList);
        const dynamic = featureKeys.map((k) => ({ key: k, header: k, accessor: (row) => featureCell(row, k), }));
        return [...base, ...dynamic];
    }, [modelValidationLogDataList]);

    const rows = useMemo(
        () => (Array.isArray(modelValidationLogDataList) ? modelValidationLogDataList : []),
        [modelValidationLogDataList]
    );

    return (
        <ThemeProvider theme={mainTheme}>
            <Dialog open fullWidth maxWidth={rows.length ? 'lg' : 'sm'}>
                <DialogTitle disableTypography className='border-b flex flex-col px-0'>
                    <div className={'flex items-center justify-between gap-32 px-32 ' + (rows.length ? '' : '')}>
                        <div className='whitespace-nowrap'>History</div>
                        <Button label='Close' onClick={() => setPopup(null)} />
                    </div>
                </DialogTitle>
                <DialogContent className='w-full flex flex-col p-20 overflow-hidden' style={{ height: rows.length ? '520px' : 'auto' }}>
                    <ConfigurableDataTable rows={rows} columns={columns} searchable={false} />
                </DialogContent>
            </Dialog>
        </ThemeProvider>
    );
}